import React, { useState, useEffect, useCallback, useRef } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import CircularProgress from '@mui/material/CircularProgress';
import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import GroupIcon from '@mui/icons-material/Group';
import agent from '../../agent';
import { getRooms } from '../../services/chat';
import { DEFAULT_THEME_COLOR } from '../../constants/theme';
import { BOTTOM_NAVIGATION_VISIBILITY } from '../../constants/actionTypes';

const NewDmView = ({ currentUser, setBottomNavigationVisible }) => {
  const navigate = useNavigate();
  const currentUsername = currentUser?.username || '';

  const [tab, setTab] = useState(0);
  const [query, setQuery] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [users, setUsers] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    setBottomNavigationVisible(false);
    return () => setBottomNavigationVisible(true);
  }, []);

  useEffect(() => {
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, []);

  const loadUsers = useCallback(async (q) => {
    setIsLoading(true);
    try {
      const data = await agent.Profile.getAll();
      const profiles = (data?.profiles || data || []).filter(
        u => u.username !== currentUsername && (!q || u.username?.toLowerCase().includes(q.toLowerCase())),
      );
      setUsers(profiles);
    } finally {
      setIsLoading(false);
    }
  }, [currentUsername]);

  const loadRooms = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getRooms(currentUsername);
      setRooms(data.filter(r => r.type === 'group'));
    } finally {
      setIsLoading(false);
    }
  }, [currentUsername]);

  useEffect(() => {
    if (tab === 0) {
      loadUsers(query);
    } else {
      loadRooms();
    }
  }, [tab, query]);

  const handleQueryChange = (e) => {
    const val = e.target.value;
    setInputValue(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setQuery(val), 300);
  };

  const filteredRooms = query
    ? rooms.filter(r => r.name?.toLowerCase().includes(query.toLowerCase()))
    : rooms;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0d1b2a 0%, #091420 100%)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', px: 1, pt: 2, pb: 1 }}>
        <IconButton onClick={() => navigate('/chat')} sx={{ color: 'white' }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
          New conversation
        </Typography>
      </Box>

      <Box sx={{ px: 2, pb: 1 }}>
        <TextField
          fullWidth
          placeholder="Search…"
          value={inputValue}
          onChange={handleQueryChange}
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              color: 'white',
              backgroundColor: 'rgba(255,255,255,0.06)',
              '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
              '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.25)' },
              '&.Mui-focused fieldset': { borderColor: DEFAULT_THEME_COLOR },
            },
            '& .MuiInputBase-input::placeholder': { color: 'rgba(255,255,255,0.35)' },
          }}
        />
      </Box>

      <Tabs
        value={tab}
        onChange={(_, v) => { setTab(v); setInputValue(''); setQuery(''); }}
        sx={{
          px: 2,
          '& .MuiTab-root': { color: 'rgba(255,255,255,0.5)', textTransform: 'none' },
          '& .Mui-selected': { color: DEFAULT_THEME_COLOR },
          '& .MuiTabs-indicator': { backgroundColor: DEFAULT_THEME_COLOR },
        }}
      >
        <Tab label="People" />
        <Tab label="Groups" />
      </Tabs>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
          <CircularProgress sx={{ color: DEFAULT_THEME_COLOR }} size={28} />
        </Box>
      ) : tab === 0 ? (
        <Box>
          {users.length === 0 ? (
            <Typography sx={{ color: 'rgba(255,255,255,0.4)', textAlign: 'center', pt: 4 }}>No users found</Typography>
          ) : users.map(user => (
            <Box
              key={user.username}
              onClick={() => navigate(`/chat/dm/${user.username}`, { state: { dmUsername: user.username, roomName: user.username } })}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2,
                py: 1.25,
                mx: 1,
                my: 0.5,
                borderRadius: 2,
                cursor: 'pointer',
                '&:hover': { backgroundColor: 'rgba(54,201,162,0.08)' },
              }}
            >
              <Avatar src={user.imageUrl} alt={user.username} sx={{ width: 40, height: 40 }} />
              <Box>
                <Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>{user.username}</Typography>
                {user.bio && (
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 220 }}>
                    {user.bio}
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      ) : (
        <Box>
          {filteredRooms.length === 0 ? (
            <Typography sx={{ color: 'rgba(255,255,255,0.4)', textAlign: 'center', pt: 4 }}>No groups found</Typography>
          ) : filteredRooms.map(room => (
            <Box
              key={room.id}
              onClick={() => navigate(`/chat/${room.id}`, { state: { roomName: room.name } })}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2,
                py: 1.25,
                mx: 1,
                my: 0.5,
                borderRadius: 2,
                cursor: 'pointer',
                '&:hover': { backgroundColor: 'rgba(54,201,162,0.08)' },
              }}
            >
              <Box sx={{ width: 40, height: 40, borderRadius: '50%', backgroundColor: DEFAULT_THEME_COLOR, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <GroupIcon sx={{ color: 'white', fontSize: 20 }} />
              </Box>
              <Box>
                <Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>{room.name || 'Group'}</Typography>
                {room.lastMessage && (
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)', display: 'block' }}>
                    {room.lastMessage.sender_username}: {room.lastMessage.content}
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

const mapStateToProps = state => ({
  currentUser: state.common.currentUser,
});

const mapDispatchToProps = dispatch => ({
  setBottomNavigationVisible: isVisible => dispatch({ type: BOTTOM_NAVIGATION_VISIBILITY, payload: isVisible }),
});

export default connect(mapStateToProps, mapDispatchToProps)(NewDmView);
